package com.devi3east.bashbuddy.auth.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.auth.domain.models.SignUpStep
import com.devi3east.bashbuddy.auth.requests.SignUpRequest
import com.devi3east.bashbuddy.auth.ui.components.StepProgressBar
import com.devi3east.bashbuddy.auth.ui.components.steps.SignUpAccountStep
import com.devi3east.bashbuddy.auth.ui.components.steps.SignUpDetailsStep
import com.devi3east.bashbuddy.auth.ui.components.steps.SignUpLegalStep
import com.devi3east.bashbuddy.auth.ui.components.steps.SignUpSupportStep
import com.devi3east.bashbuddy.common.domain.models.Address
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.footer.ui.components.FooterCard
import kotlinx.datetime.Instant

@Composable
fun SignUpScreenRoute(
    appState: AppState
) {
    val uiState by appState.authViewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        appState.authViewModel.clearError()
        appState.authViewModel.loadOrganizations()
    }

    SignUpScreen(
        uiState = uiState,
        organizations = uiState.organizations,
        onBack = {
            val popped = appState.navController.popBackStack()

            if (!popped) {
                appState.navController.navigate(Screen.EventsScreen.route) {
                    popUpTo(0)
                    launchSingleTop = true
                }
            }
        },
        onSignUp = { request -> appState.authViewModel.signUp(request) },
        onSwitchToSignIn = {
            appState.navController.navigate(Screen.SignInScreen.route) {
                popUpTo(Screen.SignUpScreen.route) { inclusive = true }
            }
        },
        onNavigateToTerms = {
            appState.navController.navigate(Screen.TermsOfServiceScreen.route) {
                launchSingleTop = true
                restoreState = true
            }
        },
        onNavigateToPrivacyPolicy = {
            appState.navController.navigate(Screen.PrivacyPolicyScreen.route) {
                launchSingleTop = true
                restoreState = true
            }
        },
        onNavigateToContact = { appState.navController.navigate(Screen.ContactUsScreen.route) }
    )
}

@Composable
fun SignUpScreen(
    uiState: AuthUiState,
    organizations: List<Organization>,
    onBack: () -> Unit,
    onSignUp: (SignUpRequest) -> Unit,
    onSwitchToSignIn: () -> Unit,
    onNavigateToTerms: () -> Unit,
    onNavigateToPrivacyPolicy: () -> Unit,
    onNavigateToContact: () -> Unit
) {
    var currentStep by rememberSaveable { mutableStateOf(SignUpStep.ACCOUNT) }

    var selectedTab by rememberSaveable { mutableStateOf("User") }
    var isOrganization by rememberSaveable { mutableStateOf(false) }
    var hasOrganizationAuthority by rememberSaveable { mutableStateOf(false) }

    var email by rememberSaveable { mutableStateOf("") }
    var displayName by rememberSaveable { mutableStateOf("") }
    var password by rememberSaveable { mutableStateOf("") }
    var confirmPassword by rememberSaveable { mutableStateOf("") }
    var passwordVisible by rememberSaveable { mutableStateOf(false) }
    var confirmPasswordVisible by rememberSaveable { mutableStateOf(false) }

    var organizationName by rememberSaveable { mutableStateOf("") }
    var contactPerson by rememberSaveable { mutableStateOf("") }
    var organizationPhone by rememberSaveable { mutableStateOf("") }
    var organizationAddressLine1 by rememberSaveable { mutableStateOf("") }
    var organizationCity by rememberSaveable { mutableStateOf("") }
    var organizationState by rememberSaveable { mutableStateOf("") }
    var organizationZip by rememberSaveable { mutableStateOf("") }

    var name by rememberSaveable { mutableStateOf("") }
    var phone by rememberSaveable { mutableStateOf("") }
    var addressLine1 by rememberSaveable { mutableStateOf("") }
    var city by rememberSaveable { mutableStateOf("") }
    var state by rememberSaveable { mutableStateOf("") }
    var zip by rememberSaveable { mutableStateOf("") }

    var selectedOrganization by rememberSaveable { mutableStateOf<Organization?>(null) }

    var certifiedOfAge by rememberSaveable { mutableStateOf(false) }
    var agreedToTerms by rememberSaveable { mutableStateOf(false) }

    var dateOfBirthInstant by rememberSaveable(
        stateSaver = Saver(
            save = { it?.toEpochMilliseconds() },
            restore = { it.let(Instant::fromEpochMilliseconds) }
        )
    ) { mutableStateOf<Instant?>(null) }

    var hasSubmitted by remember { mutableStateOf(false) }

    val displayNameInteractionSource = remember { MutableInteractionSource() }
    val scrollState = rememberScrollState()

    // Validation
    val isPasswordValid = password.length >= 8
    val doPasswordsMatch = password == confirmPassword
    val orgPhoneDigits = organizationPhone.filter(Char::isDigit)
    val userPhoneDigits = phone.filter(Char::isDigit)

    LaunchedEffect(selectedTab) {
        isOrganization = selectedTab == "Organization"
        if (!isOrganization) hasOrganizationAuthority = false
    }

    LaunchedEffect(uiState.errorMessage) {
        if (!uiState.errorMessage.isNullOrBlank()) hasSubmitted = false
    }

    val canProceedFromAccount = email.isNotBlank() && displayName.isNotBlank() && isPasswordValid && doPasswordsMatch
    val canProceedFromDetails =
        if (isOrganization) organizationName.isNotBlank() && contactPerson.isNotBlank() && orgPhoneDigits.length == 10 && organizationZip.length == 5
        else name.isNotBlank() && userPhoneDigits.length == 10 && zip.length == 5
    val canSubmit = canProceedFromAccount && canProceedFromDetails &&
            agreedToTerms && if (!isOrganization) (certifiedOfAge && dateOfBirthInstant != null) else hasOrganizationAuthority

    // Total steps based on user type
    val totalSteps = if (isOrganization) 3 else 4
    val currentStepIndex = when (currentStep) {
        SignUpStep.ACCOUNT -> 0
        SignUpStep.DETAILS -> 1
        SignUpStep.SUPPORT -> 2
        SignUpStep.LEGAL -> if (isOrganization) 2 else 3
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding),
            contentAlignment = Alignment.TopCenter
        ) {
            Box(
                modifier = Modifier
                    .widthIn(min = 300.dp, max = 500.dp)
                    .padding(16.dp)
            ) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(16.dp))
                        .background(MaterialTheme.colorScheme.surface)
                        .imePadding()
                ) {
                    Column(
                        modifier = Modifier
                            .padding(24.dp)
                            .verticalScroll(scrollState),
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        StepProgressBar(
                            currentStepIndex = currentStepIndex,
                            totalSteps = totalSteps,
                            modifier = Modifier.fillMaxWidth()
                        )

                        when (currentStep) {
                            SignUpStep.ACCOUNT -> SignUpAccountStep(
                                uiState = uiState,
                                selectedTab = selectedTab,
                                onTabSelected = { selectedTab = it },
                                email = email,
                                onEmailChange = { email = it },
                                password = password,
                                onPasswordChange = { password = it },
                                confirmPassword = confirmPassword,
                                onConfirmPasswordChange = { confirmPassword = it },
                                displayName = displayName,
                                onDisplayNameChange = { displayName = it },
                                passwordVisible = passwordVisible,
                                onTogglePasswordVisibility = { passwordVisible = !passwordVisible },
                                confirmPasswordVisible = confirmPasswordVisible,
                                onToggleConfirmPasswordVisibility = { confirmPasswordVisible = !confirmPasswordVisible },
                                isPasswordValid = isPasswordValid,
                                doPasswordsMatch = doPasswordsMatch,
                                displayNameInteractionSource = displayNameInteractionSource,
                                hasSubmitted = hasSubmitted
                            )

                            SignUpStep.DETAILS -> SignUpDetailsStep(
                                uiState = uiState,
                                isOrganization = isOrganization,
                                hasSubmitted = hasSubmitted,
                                organizationName = organizationName,
                                onOrganizationNameChange = { organizationName = it },
                                contactPerson = contactPerson,
                                onContactPersonChange = { contactPerson = it },
                                organizationPhone = organizationPhone,
                                onOrganizationPhoneChange = { organizationPhone = it },
                                organizationAddress = organizationAddressLine1,
                                onOrganizationAddressChange = { organizationAddressLine1 = it },
                                organizationCity = organizationCity,
                                onOrganizationCityChange = { organizationCity = it },
                                organizationState = organizationState,
                                onOrganizationStateChange = { organizationState = it },
                                organizationZip = organizationZip,
                                onOrganizationZipChange = { organizationZip = it },
                                name = name,
                                onNameChange = { name = it },
                                phone = phone,
                                onPhoneChange = { phone = it },
                                address = addressLine1,
                                onAddressChange = { addressLine1 = it },
                                city = city,
                                onCityChange = { city = it },
                                state = state,
                                onStateChange = { state = it },
                                zip = zip,
                                onZipChange = { zip = it }
                            )

                            SignUpStep.SUPPORT -> SignUpSupportStep(
                                uiState = uiState,
                                organizations = organizations,
                                selectedOrganization = selectedOrganization,
                                onOrganizationSelected = { selectedOrganization = it }
                            )

                            SignUpStep.LEGAL -> SignUpLegalStep(
                                uiState = uiState,
                                dateOfBirth = dateOfBirthInstant,
                                onDateOfBirthChange = { dateOfBirthInstant = it },
                                certifiedOfAge = certifiedOfAge,
                                onCertifiedOfAgeChange = { certifiedOfAge = it },
                                agreedToTerms = agreedToTerms,
                                onAgreedToTermsChange = { agreedToTerms = it },
                                onNavigateToTerms = onNavigateToTerms,
                                isOrganization = isOrganization,
                                hasAuthority = hasOrganizationAuthority,
                                onAuthorityChange = { hasOrganizationAuthority = it },
                                organizationName = if (isOrganization) organizationName else null,
                                hasSubmitted = hasSubmitted
                            )
                        }

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.End,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            TextButton(
                                onClick = {
                                    currentStep = when (currentStep) {
                                        SignUpStep.ACCOUNT -> {
                                            onBack()
                                            currentStep
                                        }
                                        SignUpStep.DETAILS -> SignUpStep.ACCOUNT
                                        SignUpStep.SUPPORT -> SignUpStep.DETAILS
                                        SignUpStep.LEGAL -> if (isOrganization) SignUpStep.DETAILS else SignUpStep.SUPPORT
                                    }
                                }
                            ) {
                                Text("Back")
                            }

                            Spacer(modifier = Modifier.width(8.dp))

                            when (currentStep) {
                                SignUpStep.ACCOUNT,
                                SignUpStep.DETAILS -> {
                                    val enabled = when (currentStep) {
                                        SignUpStep.ACCOUNT -> canProceedFromAccount
                                        SignUpStep.DETAILS -> canProceedFromDetails
                                        else -> false
                                    }
                                    Button(
                                        onClick = {
                                            currentStep = when (currentStep) {
                                                SignUpStep.ACCOUNT -> SignUpStep.DETAILS
                                                SignUpStep.DETAILS -> if (isOrganization) SignUpStep.LEGAL else SignUpStep.SUPPORT
                                                else -> currentStep
                                            }
                                        },
                                        enabled = enabled,
                                        modifier = Modifier.height(54.dp),
                                        shape = RoundedCornerShape(12.dp)
                                    ) {
                                        Text("Next")
                                    }
                                }

                                SignUpStep.SUPPORT -> {
                                    if (selectedOrganization != null) {
                                        Button(
                                            onClick = { currentStep = SignUpStep.LEGAL },
                                            modifier = Modifier.height(54.dp),
                                            shape = RoundedCornerShape(12.dp)
                                        ) {
                                            Text("Next")
                                        }
                                    } else {
                                        TextButton(
                                            onClick = {
                                                selectedOrganization = null
                                                currentStep = SignUpStep.LEGAL
                                            }
                                        ) {
                                            Text("Skip")
                                        }
                                    }
                                }

                                SignUpStep.LEGAL -> {
                                    Button(
                                        onClick = {
                                            hasSubmitted = true

                                            val organizationAddress =
                                                if (organizationAddressLine1.isNotBlank()) Address(
                                                    line1 = organizationAddressLine1.trim(),
                                                    city = organizationCity.trim(),
                                                    state = organizationState.trim().uppercase(),
                                                    zipCode = organizationZip.trim()
                                                ) else null

                                            val personalAddress =
                                                if (addressLine1.isNotBlank()) Address(
                                                    line1 = addressLine1.trim(),
                                                    city = city.trim(),
                                                    state = state.trim().uppercase(),
                                                    zipCode = zip.trim()
                                                ) else null

                                            val request = SignUpRequest(
                                                email = email.trim(),
                                                displayName = displayName.trim(),
                                                password = password.trim(),
                                                confirmPassword = confirmPassword.trim(),
                                                isOrganization = isOrganization,
                                                hasOrganizationAuthority = if (isOrganization) hasOrganizationAuthority else null,
                                                organizationName = if (isOrganization) organizationName.trim() else null,
                                                contactPerson = if (isOrganization) contactPerson.trim() else null,
                                                organizationPhone = if (isOrganization) organizationPhone.trim() else null,
                                                organizationAddress = organizationAddress,
                                                name = if (!isOrganization) name.trim() else null,
                                                phone = if (!isOrganization) phone.trim() else null,
                                                address = personalAddress,
                                                dateOfBirthInstant = if (!isOrganization) dateOfBirthInstant else null,
                                                certifiedOfAge = if (!isOrganization) certifiedOfAge else false,
                                                agreedToTermsOfService = agreedToTerms,
                                                affiliatedOrganization = selectedOrganization?._id
                                            )

                                            onSignUp(request)
                                        },
                                        enabled = canSubmit && !hasSubmitted,
                                        modifier = Modifier.height(54.dp),
                                        shape = RoundedCornerShape(12.dp)
                                    ) {
                                        if (hasSubmitted) {
                                            CircularProgressIndicator(
                                                color = MaterialTheme.colorScheme.onPrimary,
                                                strokeWidth = 2.dp,
                                                modifier = Modifier.size(22.dp)
                                            )
                                        } else {
                                            Text(
                                                text = "Sign Up",
                                                fontSize = 16.sp,
                                                fontWeight = FontWeight.SemiBold
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        if (currentStep == SignUpStep.ACCOUNT) {
                            HorizontalDivider(
                                modifier = Modifier,
                                thickness = 1.dp,
                                color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
                            )

                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text(
                                    text = "Already have an account?",
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                )
                                TextButton(onClick = onSwitchToSignIn) {
                                    Text(
                                        text = "Click here to sign in!",
                                        fontWeight = FontWeight.Medium,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        }

                        HorizontalDivider(
                            thickness = 1.dp,
                            color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
                        )

                        Text(
                            text = "Personal information such as your name, phone number, address, and date of birth is collected to verify identity, confirm eligibility for age-restricted events, and ensure the secure ownership of tickets. This information is never sold or shared with third parties. Providing accurate details helps us maintain a safe, trustworthy, and compliant community for all participants.",
                            style = MaterialTheme.typography.bodySmall.copy(
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            ),
                            modifier = Modifier.padding(vertical = 4.dp),
                            textAlign = TextAlign.Center
                        )

                        FooterCard(
                            onNavigateToTerms = onNavigateToTerms,
                            onNavigateToPrivacyPolicy = onNavigateToPrivacyPolicy,
                            onNavigateToContact = onNavigateToContact
                        )
                    }
                }
            }
        }
    }
}

/*
@Composable
fun SignUpScreen(
    uiState: AuthUiState,
    snackbarHostState: SnackbarHostState,
    organizations: List<Organization>,
    onBack: () -> Unit,
    onSignUp: (SignUpRequest) -> Unit,
    onSwitchToSignIn: () -> Unit,
    onNavigateToTerms: () -> Unit,
    onNavigateToPrivacyPolicy: () -> Unit,
    onNavigateToContact: () -> Unit
) {
// ─────────────────────────────────────────────
// Account type
// ─────────────────────────────────────────────
    val toggleOptions = listOf("User", "Organization")
    var selectedTab by rememberSaveable { mutableStateOf("User") }
    var isOrganization by rememberSaveable { mutableStateOf(false) }
    var hasOrganizationAuthority by rememberSaveable { mutableStateOf(false) }

// ─────────────────────────────────────────────
// Core credentials
// ─────────────────────────────────────────────
    var email by rememberSaveable { mutableStateOf("") }
    var displayName by rememberSaveable { mutableStateOf("") }
    var password by rememberSaveable { mutableStateOf("") }
    var confirmPassword by rememberSaveable { mutableStateOf("") }
    var passwordVisible by rememberSaveable { mutableStateOf(false) }
    var confirmPasswordVisible by rememberSaveable { mutableStateOf(false) }

// ─────────────────────────────────────────────
// Organization fields
// ─────────────────────────────────────────────
    var organizationName by rememberSaveable { mutableStateOf("") }
    var contactPerson by rememberSaveable { mutableStateOf("") }
    var organizationPhone by rememberSaveable { mutableStateOf("") }
    var organizationAddressLine1 by rememberSaveable { mutableStateOf("") }
    var organizationCity by rememberSaveable { mutableStateOf("") }
    var organizationState by rememberSaveable { mutableStateOf("") }
    var organizationZip by rememberSaveable { mutableStateOf("") }

// ─────────────────────────────────────────────
// User fields
// ─────────────────────────────────────────────
    var name by rememberSaveable { mutableStateOf("") }
    var phone by rememberSaveable { mutableStateOf("") }
    var addressLine1 by rememberSaveable { mutableStateOf("") }
    var city by rememberSaveable { mutableStateOf("") }
    var state by rememberSaveable { mutableStateOf("") }
    var zip by rememberSaveable { mutableStateOf("") }

    var selectedOrganization by rememberSaveable { mutableStateOf<Organization?>(null) }

// ─────────────────────────────────────────────
// Age / legal
// ─────────────────────────────────────────────
    var certifiedOfAge by rememberSaveable { mutableStateOf(false) }
    var agreedToTerms by rememberSaveable { mutableStateOf(false) }

    var dateOfBirthInstant by rememberSaveable(
        stateSaver = Saver(
            save = { it?.toEpochMilliseconds() },
            restore = { Instant.fromEpochMilliseconds(it) }
        )
    ) { mutableStateOf<Instant?>(null) }

// ─────────────────────────────────────────────
// UI-only state
// ─────────────────────────────────────────────
    var showDatePicker by remember { mutableStateOf(false) }
    var hasSubmitted by remember { mutableStateOf(false) }

    val displayNameInteractionSource = remember { MutableInteractionSource() }
    val isDisplayNameFocused by displayNameInteractionSource.collectIsFocusedAsState()

    val scrollState = rememberScrollState()

// ─────────────────────────────────────────────
// Derived validation state
// ─────────────────────────────────────────────
    val isPasswordValid = password.length >= 8
    val doPasswordsMatch = password == confirmPassword

    val orgPhoneDigits = organizationPhone.filter { it.isDigit() }
    val userPhoneDigits = phone.filter { it.isDigit() }

// ─────────────────────────────────────────────
// Side effects
// ─────────────────────────────────────────────
    LaunchedEffect(selectedTab) {
        isOrganization = selectedTab == "Organization"
        if (!isOrganization) hasOrganizationAuthority = false
    }

    LaunchedEffect(uiState.errorMessage) {
        if (!uiState.errorMessage.isNullOrBlank()) {
            hasSubmitted = false
        }
    }

// ─────────────────────────────────────────────
// Submission eligibility
// ─────────────────────────────────────────────
    val canSubmit: Boolean = run {
        val basicValid =
            isPasswordValid &&
                    doPasswordsMatch &&
                    email.isNotBlank() &&
                    displayName.isNotBlank()

        if (isOrganization) {
            basicValid &&
                    organizationName.isNotBlank() &&
                    contactPerson.isNotBlank() &&
                    orgPhoneDigits.length == 10 &&
                    organizationAddressLine1.isNotBlank() &&
                    organizationCity.isNotBlank() &&
                    organizationState.length == 2 &&
                    organizationZip.length == 5 &&
                    hasOrganizationAuthority &&
                    agreedToTerms
        } else {
            basicValid &&
                    name.isNotBlank() &&
                    userPhoneDigits.length == 10 &&
                    addressLine1.isNotBlank() &&
                    city.isNotBlank() &&
                    state.length == 2 &&
                    zip.length == 5 &&
                    dateOfBirthInstant != null &&
                    certifiedOfAge &&
                    agreedToTerms
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding),
            contentAlignment = Alignment.TopCenter
        ) {
            Box(
                modifier = Modifier
                    .widthIn(min = 300.dp, max = 500.dp)
                    .padding(16.dp)
            ) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(16.dp))
                        .background(MaterialTheme.colorScheme.surface)
                        .imePadding()
                ) {
                    Column(
                        modifier = Modifier
                            .padding(24.dp)
                            .verticalScroll(scrollState),
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = "Join the Party!",
                            style = MaterialTheme.typography.headlineMedium.copy(
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            ),
                            textAlign = TextAlign.Center
                        )

                        Text(
                            text = "Sign up to support causes that matter — or share your own.",
                            style = MaterialTheme.typography.bodyMedium.copy(
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                lineHeight = 20.sp
                            ),
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(horizontal = 8.dp)
                        )

                        HorizontalDivider(
                            thickness = 1.dp,
                            color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
                        )

                        // --- Toggle row replaces the old checkbox
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            toggleOptions.forEach { label ->
                                val isSelected = selectedTab == label
                                val backgroundColor by animateColorAsState(
                                    if (isSelected) MaterialTheme.colorScheme.primaryContainer
                                    else MaterialTheme.colorScheme.surface.copy(alpha = 0.6f)
                                )
                                val textColor by animateColorAsState(
                                    if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer
                                    else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                                )

                                Surface(
                                    color = backgroundColor,
                                    shape = RoundedCornerShape(12.dp),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                                    modifier = Modifier
                                        .weight(1f)
                                        .clickable { selectedTab = label }
                                        .heightIn(min = 48.dp)
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(vertical = 8.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = label,
                                            color = textColor,
                                            style = MaterialTheme.typography.bodyLarge,
                                            fontWeight = FontWeight.Bold,
                                            textAlign = TextAlign.Center
                                        )
                                    }
                                }
                            }
                        }

                        // --- Account type description
                        AnimatedContent(
                            targetState = selectedTab,
                            label = "AccountTypeDescription"
                        ) { tab ->
                            val description = when (tab) {
                                "Organization" -> "Organizations can create and manage their own events, but cannot purchase tickets."
                                else -> "Users can browse events and purchase tickets, but cannot host events."
                            }
                            Text(
                                text = description,
                                style = MaterialTheme.typography.bodySmall.copy(
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                ),
                                textAlign = TextAlign.Center,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 4.dp)
                            )
                        }

                        OutlinedTextField(
                            value = email,
                            onValueChange = { email = it },
                            enabled = !hasSubmitted,
                            label = { Text("Email") },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        OutlinedTextField(
                            value = password,
                            onValueChange = { password = it },
                            enabled = !hasSubmitted,
                            label = { Text("Password") },
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            trailingIcon = {
                                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                    Icon(
                                        imageVector = if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                                        contentDescription = null
                                    )
                                }
                            },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        if (password.isNotEmpty() && !isPasswordValid) {
                            Text(
                                text = "Password must be at least 8 characters.",
                                style = MaterialTheme.typography.bodySmall.copy(color = MaterialTheme.colorScheme.error),
                                modifier = Modifier.align(Alignment.Start)
                            )
                        }

                        OutlinedTextField(
                            value = confirmPassword,
                            onValueChange = { confirmPassword = it },
                            enabled = !hasSubmitted,
                            label = { Text("Confirm Password") },
                            visualTransformation = if (confirmPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            trailingIcon = {
                                IconButton(onClick = {
                                    confirmPasswordVisible = !confirmPasswordVisible
                                }) {
                                    Icon(
                                        imageVector = if (confirmPasswordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                                        contentDescription = null
                                    )
                                }
                            },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        if (confirmPassword.isNotEmpty() && !doPasswordsMatch) {
                            Text(
                                text = "Passwords do not match.",
                                style = MaterialTheme.typography.bodySmall.copy(color = MaterialTheme.colorScheme.error),
                                modifier = Modifier.align(Alignment.Start)
                            )
                        }

                        OutlinedTextField(
                            value = displayName,
                            onValueChange = { displayName = it.take(20) },
                            enabled = !hasSubmitted,
                            label = { Text("Display Name") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp),
                            interactionSource = displayNameInteractionSource
                        )

                        if (isDisplayNameFocused) {
                            Text(
                                text = "Maximum 20 characters.",
                                style = MaterialTheme.typography.bodySmall.copy(color = MaterialTheme.colorScheme.onSurfaceVariant),
                                modifier = Modifier.align(Alignment.Start)
                            )
                        }

                        if (isOrganization) {
                            OutlinedTextField(
                                value = organizationName,
                                onValueChange = { organizationName = it },
                                enabled = !hasSubmitted,
                                label = { Text("Organization Name") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            OutlinedTextField(
                                value = contactPerson,
                                onValueChange = { contactPerson = it },
                                enabled = !hasSubmitted,
                                label = { Text("Contact Person") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            OutlinedTextField(
                                value = organizationPhone.take(10),
                                onValueChange = { organizationPhone = it.take(10) },
                                enabled = !hasSubmitted,
                                label = { Text("Phone") },
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                                visualTransformation = PhoneNumberTransformation(),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            OutlinedTextField(
                                value = organizationAddressLine1,
                                onValueChange = { organizationAddressLine1 = it },
                                enabled = !hasSubmitted,
                                label = { Text("Address") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = organizationCity,
                                    onValueChange = { organizationCity = it },
                                    enabled = !hasSubmitted,
                                    label = { Text("City") },
                                    singleLine = true,
                                    modifier = Modifier.weight(1f),
                                    shape = RoundedCornerShape(12.dp)
                                )
                                OutlinedTextField(
                                    value = organizationState.take(2),
                                    onValueChange = { organizationState = it.take(2).uppercase() },
                                    enabled = !hasSubmitted,
                                    label = { Text("State") },
                                    singleLine = true,
                                    modifier = Modifier.weight(1f),
                                    shape = RoundedCornerShape(12.dp)
                                )
                            }
                            OutlinedTextField(
                                value = organizationZip.take(5),
                                onValueChange = { organizationZip = it.take(5) },
                                enabled = !hasSubmitted,
                                label = { Text("Zip Code") },
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Checkbox(
                                    checked = hasOrganizationAuthority,
                                    onCheckedChange = { hasOrganizationAuthority = it },
                                    enabled = !hasSubmitted
                                )
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(
                                    text = "I certify that I have the authority to sign up on behalf of this organization as the contact person listed above.",
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                        } else {
                            OutlinedTextField(
                                value = name,
                                onValueChange = { name = it },
                                enabled = !hasSubmitted,
                                label = { Text("Full Name") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            OutlinedTextField(
                                value = phone.take(10),
                                onValueChange = { phone = it.take(10) },
                                enabled = !hasSubmitted,
                                label = { Text("Phone") },
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                                visualTransformation = PhoneNumberTransformation(),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )
                            OutlinedTextField(
                                value = addressLine1,
                                onValueChange = { addressLine1 = it },
                                enabled = !hasSubmitted,
                                label = { Text("Address") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            Row(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {

                                OutlinedTextField(
                                    value = city,
                                    onValueChange = { city = it },
                                    enabled = !hasSubmitted,
                                    label = { Text("City") },
                                    singleLine = true,
                                    modifier = Modifier.weight(1f),
                                    shape = RoundedCornerShape(12.dp)
                                )

                                OutlinedTextField(
                                    value = state.take(2),
                                    onValueChange = { state = it.take(2).uppercase() },
                                    enabled = !hasSubmitted,
                                    label = { Text("State") },
                                    singleLine = true,
                                    modifier = Modifier.weight(1f),
                                    shape = RoundedCornerShape(12.dp)
                                )
                            }

                            OutlinedTextField(
                                value = zip.take(5),
                                onValueChange = { zip = it.take(5) },
                                enabled = !hasSubmitted,
                                label = { Text("Zip Code") },
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(12.dp)
                            )

                            OrganizationPicker(
                                organizations = organizations,
                                selectedOrganization = selectedOrganization,
                                onSelected = { selectedOrganization = it }
                            )

                            DateInput(
                                dateInstant = dateOfBirthInstant,
                                label = "Date of Birth",
                                onDateChange = { dateOfBirthInstant = it }
                            )

                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Checkbox(
                                    checked = certifiedOfAge,
                                    onCheckedChange = { certifiedOfAge = it },
                                    enabled = !hasSubmitted
                                )

                                Spacer(modifier = Modifier.width(6.dp))

                                Text(
                                    text = "By continuing, I certify that I am at least 18 years of age.",
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                        }

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Checkbox(
                                checked = agreedToTerms,
                                onCheckedChange = { agreedToTerms = it },
                                enabled = !hasSubmitted
                            )

                            Spacer(modifier = Modifier.width(6.dp))

                            TermsOfServiceText(onNavigateToTerms)
                        }

                        if (!uiState.errorMessage.isNullOrBlank()) {
                            Text(
                                text = uiState.errorMessage,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Medium),
                                textAlign = TextAlign.Center
                            )
                        }

                        Button(
                            onClick = {
                                hasSubmitted = true

                                val organizationAddress =
                                    if (organizationAddressLine1.isNotBlank()) {
                                        Address(
                                            line1 = organizationAddressLine1,
                                            city = organizationCity,
                                            state = organizationState,
                                            zipCode = organizationZip
                                        )
                                    } else null

                                val personalAddress = if (addressLine1.isNotBlank()) {
                                    Address(
                                        line1 = addressLine1,
                                        city = city,
                                        state = state,
                                        zipCode = zip
                                    )
                                } else null

                                val request = SignUpRequest(
                                    email = email,
                                    displayName = displayName,
                                    password = password,
                                    confirmPassword = confirmPassword,
                                    isOrganization = isOrganization,
                                    hasOrganizationAuthority = if (isOrganization) hasOrganizationAuthority else null,
                                    organizationName = if (isOrganization) organizationName else null,
                                    contactPerson = if (isOrganization) contactPerson else null,
                                    organizationPhone = if (isOrganization) organizationPhone else null,
                                    organizationAddress = organizationAddress,
                                    name = if (!isOrganization) name else null,
                                    phone = if (!isOrganization) phone else null,
                                    address = personalAddress,
                                    dateOfBirthInstant = if (!isOrganization) dateOfBirthInstant else null,
                                    certifiedOfAge = if (!isOrganization) certifiedOfAge else false,
                                    agreedToTermsOfService = agreedToTerms,
                                    affiliatedOrganization = selectedOrganization?._id
                                )
                                onSignUp(request)
                            },
                            enabled = canSubmit && !hasSubmitted,
                            modifier = Modifier.height(54.dp),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            if (hasSubmitted) {
                                CircularProgressIndicator(
                                    color = MaterialTheme.colorScheme.onPrimary,
                                    strokeWidth = 2.dp,
                                    modifier = Modifier.size(22.dp)
                                )
                            } else {
                                Text(
                                    text = "Sign Up",
                                    fontSize = 16.sp,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }

                        Text(
                            text = "Personal information such as your name, phone number, address, and date of birth is collected to verify identity, confirm eligibility for age-restricted events, and ensure the secure ownership of tickets. This information is never sold or shared with third parties. Providing accurate details helps us maintain a safe, trustworthy, and compliant community for all participants.",
                            style = MaterialTheme.typography.bodySmall.copy(
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            ),
                            modifier = Modifier.padding(vertical = 4.dp),
                            textAlign = TextAlign.Center
                        )

                        HorizontalDivider(
                            thickness = 1.dp,
                            color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
                        )

                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = "Already have an account?",
                                style = MaterialTheme.typography.bodyMedium.copy(
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            )
                            TextButton(onClick = onSwitchToSignIn) {
                                Text(
                                    text = "Click here to sign in!",
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }
                        }

                        FooterCard(
                            onNavigateToTerms = { onNavigateToTerms() },
                            onNavigateToPrivacyPolicy = { onNavigateToPrivacyPolicy() },
                            onNavigateToContact = { onNavigateToContact() }
                        )
                    }
                }
                FloatingActionButton(
                    onClick = onBack,
                    containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.85f),
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .padding(12.dp)
                        .size(40.dp)
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = "Back",
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
            }

            if (showDatePicker) {
                ModernCalendarPicker(
                    initialInstant = dateOfBirthInstant,
                    onDateSelected = { selected -> dateOfBirthInstant = selected },
                    onDismiss = { showDatePicker = false }
                )
            }
        }
    }
}*/
